import { useEffect, useMemo, useState, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { CHART_METRIC, VIEW_MODE } from './LifeCycleAnalyticsConstants';
import {
	getDefaultFilters,
	filtersToParams,
	filtersToQueryParams,
	queryParamsToFilters,
	parseSafeInt,
	clampDateRange,
} from './LifeCycleAnalyticsUtils';
import queryString from 'qs';

/**
 * URL sync, server state, priority/metric navigation, and filter draft handlers
 * for Life Cycle Analytics overview.
 */
export function useLifeCycleAnalyticsData(props: any) {
	const {
		companyConfig,
		userType,
		problemTypes,
		workOrderPriorities,
		fetchPrioritySummary,
		fetchSupplierPerformance,
		fetchMonthlyApprovalTime,
		fetchMonthlyResponseTime,
		fetchMonthlyResolutionTime,
		fetchMonthlyFirstTimeFixRate,
		fetchMonthlySpend,
		fetchProblemTypes,
		fetchWorkOrderPriorities,
	} = props;

	const history = useHistory();
	const location = useLocation();

	const { initialFilters, initialPriorityId, initialMinWoCount } = useMemo(() => {
		const search = location.search;
		const str = search && search[0] === '?' ? search.slice(1) : search;
		const qp = queryString.parse(str || '');
		const hasParams = Object.keys(qp).length > 0;
		return {
			initialFilters: hasParams ? queryParamsToFilters(qp) : getDefaultFilters(),
			initialPriorityId: qp.priorityId ? parseSafeInt(qp.priorityId, null) : null,
			initialMinWoCount: qp.minWoCount ? parseSafeInt(qp.minWoCount, 5) : 5,
		};
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const [loading, setLoading] = useState(true);
	const [chartLoading, setChartLoading] = useState(true);
	const [prioritiesLoading, setPrioritiesLoading] = useState(true);
	const [error, setError] = useState('');
	const [prioritySummary, setPrioritySummary] = useState([]);
	const [supplierPerformance, setSupplierPerformance] = useState([]);
	const [monthlyMetricData, setMonthlyMetricData] = useState([]);
	const [monthlySpend, setMonthlySpend] = useState([]);

	const [activeFilters, setActiveFilters] = useState(initialFilters);
	const [draftFilters, setDraftFilters] = useState(initialFilters);
	const [filterModalVisible, setFilterModalVisible] = useState(false);
	const [selectedPriorityId, setSelectedPriorityId] = useState(initialPriorityId);
	const [minWoCount, setMinWoCount] = useState(initialMinWoCount);
	const [viewMode, setViewMode] = useState(VIEW_MODE.ALL);
	const [chartMetric, setChartMetric] = useState(CHART_METRIC.APPROVAL_TIME);
	const [createdAtPickerDates, setCreatedAtPickerDates] = useState(null);
	const [completedAtPickerDates, setCompletedAtPickerDates] = useState(null);

	useEffect(() => {
		const params = filtersToQueryParams(activeFilters, selectedPriorityId, minWoCount);
		const search = queryString.stringify(params);
		history.replace(location.pathname + (search ? '?' + search : ''));
	}, [activeFilters, selectedPriorityId, minWoCount]); // eslint-disable-line react-hooks/exhaustive-deps

	const isAutoApprovalEnabled = useMemo(() => {
		const approvalConfig = nullSafeGetOrElse('detail.config.approvalConfig', companyConfig, {});
		return (
			nullSafeGetOrElse('autoApproveServiceRequests', approvalConfig, false) &&
			nullSafeGetOrElse('autoApproveSupplierInitiatedWorkOrders', approvalConfig, false)
		);
	}, [companyConfig]);

	const getMetricFetchFn = useCallback(
		(metricKey) => {
			switch (metricKey) {
				case CHART_METRIC.RESPONSE_TIME:
					return fetchMonthlyResponseTime;
				case CHART_METRIC.RESOLUTION_TIME:
					return fetchMonthlyResolutionTime;
				case CHART_METRIC.FIRST_TIME_FIX_RATE:
					return fetchMonthlyFirstTimeFixRate;
				default:
					return fetchMonthlyApprovalTime;
			}
		},
		[
			fetchMonthlyApprovalTime,
			fetchMonthlyResponseTime,
			fetchMonthlyResolutionTime,
			fetchMonthlyFirstTimeFixRate,
		]
	);

	const loadChartData = useCallback(
		(filters, priorityId, metricKey) => {
			setChartLoading(true);
			const params = filtersToParams(filters);
			if (priorityId !== null) {
				params.woPriorityId = priorityId;
			}
			const defaultMetric = isAutoApprovalEnabled
				? CHART_METRIC.RESPONSE_TIME
				: CHART_METRIC.APPROVAL_TIME;
			const fetchMetricFn = getMetricFetchFn(metricKey || defaultMetric);
			Promise.all([
				fetchSupplierPerformance({}, params),
				fetchMetricFn({}, params),
				fetchMonthlySpend({}, params),
			])
				.then(([supplierData, metricData, spendData]) => {
					setSupplierPerformance(supplierData || []);
					setMonthlyMetricData(metricData || []);
					setMonthlySpend(spendData || []);
				})
				.catch((e) => {
					setError((e && e.message) || String(e) || 'Failed to load analytics data.');
				})
				.finally(() => {
					setChartLoading(false);
				});
		},
		[fetchSupplierPerformance, getMetricFetchFn, fetchMonthlySpend, isAutoApprovalEnabled]
	);

	const loadAllData = useCallback(
		(filters, priorityIdHint) => {
			setLoading(true);
			setChartLoading(true);
			setError('');
			const defaultMetric = isAutoApprovalEnabled
				? CHART_METRIC.RESPONSE_TIME
				: CHART_METRIC.APPROVAL_TIME;
			setChartMetric(defaultMetric);
			const params = filtersToParams(filters);
			fetchPrioritySummary({}, params)
				.then((priorityData) => {
					const data = priorityData || [];
					setPrioritySummary(data);
					const sorted = data.slice().sort((a, b) => a.woPriorityId - b.woPriorityId);
					const hintIsValid =
						priorityIdHint !== null &&
						priorityIdHint !== undefined &&
						sorted.some((row) => row.woPriorityId === priorityIdHint);

					setViewMode(VIEW_MODE.ALL);
					setSelectedPriorityId(null);
					setSupplierPerformance([]);
					setMonthlyMetricData([]);
					setMonthlySpend([]);

					if (!hintIsValid) {
						return null;
					}
					setSelectedPriorityId(priorityIdHint);
					setViewMode(VIEW_MODE.SINGLE);
					const chartParams = filtersToParams(filters);
					chartParams.woPriorityId = priorityIdHint;
					const defaultMetricInner = isAutoApprovalEnabled
						? CHART_METRIC.RESPONSE_TIME
						: CHART_METRIC.APPROVAL_TIME;
					return Promise.all([
						fetchSupplierPerformance({}, chartParams),
						getMetricFetchFn(defaultMetricInner)({}, chartParams),
						fetchMonthlySpend({}, chartParams).catch(() => []),
					]);
				})
				.then((chartResult) => {
					if (!chartResult) {
						return;
					}
					const [supplierData, monthlyMetricResult, monthlySpendData] = chartResult;
					setSupplierPerformance(supplierData || []);
					setMonthlyMetricData(monthlyMetricResult || []);
					setMonthlySpend(monthlySpendData || []);
				})
				.catch((e) => {
					setError((e && e.message) || String(e) || 'Failed to load Life Cycle Analytics data.');
				})
				.finally(() => {
					setLoading(false);
					setChartLoading(false);
				});
		},
		[
			fetchPrioritySummary,
			fetchSupplierPerformance,
			fetchMonthlySpend,
			getMetricFetchFn,
			isAutoApprovalEnabled,
		]
	);

	useEffect(() => {
		Promise.resolve(fetchWorkOrderPriorities())
			.catch((e) => {
				console.error('Failed to load work order priorities', e);
			})
			.finally(() => {
				setPrioritiesLoading(false);
			});
		loadAllData(initialFilters, initialPriorityId);
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const priorityMap = useMemo(() => {
		return nullSafeGetOrElse('records', workOrderPriorities, {});
	}, [workOrderPriorities]);

	const openFilterModal = () => {
		setDraftFilters({ ...activeFilters });
		setFilterModalVisible(true);
	};

	const applyFilters = () => {
		setActiveFilters(draftFilters);
		setFilterModalVisible(false);
		loadAllData(draftFilters, selectedPriorityId);
	};

	const cancelFilters = () => {
		setFilterModalVisible(false);
	};

	const removeProblemTypeFilter = () => {
		const newFilters = { ...activeFilters, problemTypeIds: [] };
		setActiveFilters(newFilters);
		loadAllData(newFilters, selectedPriorityId);
	};

	const removeDateFilter = useCallback(
		(field) => {
			const newFilters = { ...activeFilters, [field]: null };
			setActiveFilters(newFilters);
			loadAllData(newFilters, selectedPriorityId);
		},
		[activeFilters, selectedPriorityId, loadAllData]
	);

	const handleChartMetricChange = useCallback(
		(metricKey) => {
			setChartMetric(metricKey);
			if (selectedPriorityId === null) return;
			setChartLoading(true);
			const params = filtersToParams(activeFilters);
			params.woPriorityId = selectedPriorityId;
			const fetchFn = getMetricFetchFn(metricKey);
			fetchFn({}, params)
				.then((data) => {
					setMonthlyMetricData(data || []);
				})
				.catch((e) => {
					setError((e && e.message) || String(e) || 'Failed to load chart data.');
				})
				.finally(() => {
					setChartLoading(false);
				});
		},
		[activeFilters, selectedPriorityId, getMetricFetchFn]
	);

	const handleArrowClick = useCallback(
		(priorityId) => {
			if (viewMode === VIEW_MODE.ALL) {
				setSelectedPriorityId(priorityId);
				setViewMode(VIEW_MODE.SINGLE);
				const defaultMetric = isAutoApprovalEnabled
					? CHART_METRIC.RESPONSE_TIME
					: CHART_METRIC.APPROVAL_TIME;
				setChartMetric(defaultMetric);
				loadChartData(activeFilters, priorityId, defaultMetric);
				return;
			}

			if (viewMode === VIEW_MODE.SINGLE) {
				if (selectedPriorityId === priorityId) {
					setViewMode(VIEW_MODE.EXPANDED);
					return;
				}
				setSelectedPriorityId(priorityId);
				loadChartData(activeFilters, priorityId, chartMetric);
				return;
			}

			if (selectedPriorityId === priorityId) {
				setViewMode(VIEW_MODE.SINGLE);
				return;
			}
			setSelectedPriorityId(priorityId);
			setViewMode(VIEW_MODE.SINGLE);
			loadChartData(activeFilters, priorityId, chartMetric);
		},
		[viewMode, selectedPriorityId, activeFilters, chartMetric, loadChartData, isAutoApprovalEnabled]
	);

	const navigateToSupplierDetail = useCallback(
		(supplierFacilityId) => {
			if (!supplierFacilityId) return;
			const filterParams = filtersToQueryParams(
				activeFilters,
				selectedPriorityId,
				minWoCount,
				viewMode
			);
			const pathName = '/' + userType + '/analytics/lifecycle/overview';
			const backlinkParts = {
				pathName: pathName,
				backLinkText: 'Back to Life Cycle Analytics',
				...filterParams,
			};
			const backlinkParams = encodeURIComponent(queryString.stringify(backlinkParts));
			history.push(
				'/' +
					userType +
					'/analytics/kpi/vendors/detail/' +
					supplierFacilityId +
					'?backlinkParams=' +
					backlinkParams
			);
		},
		[activeFilters, selectedPriorityId, minWoCount, viewMode, userType, history]
	);

	const handleRemoveCreatedAtFilter = useCallback(
		() => removeDateFilter('createdAt'),
		[removeDateFilter]
	);
	const handleRemoveCompletedAtFilter = useCallback(
		() => removeDateFilter('completedAt'),
		[removeDateFilter]
	);

	const handleProblemTypeChange = useCallback(
		(ids) => setDraftFilters((prev) => ({ ...prev, problemTypeIds: ids || [] })),
		[]
	);
	const handleCreatedAtCalendarChange = useCallback((dates) => setCreatedAtPickerDates(dates), []);
	const handleCreatedAtChange = useCallback((dates) => {
		setDraftFilters((prev) => ({ ...prev, createdAt: clampDateRange(dates) }));
		setCreatedAtPickerDates(null);
	}, []);
	const handleCreatedAtOpenChange = useCallback((open) => {
		if (!open) setCreatedAtPickerDates(null);
	}, []);

	const handleCompletedAtCalendarChange = useCallback(
		(dates) => setCompletedAtPickerDates(dates),
		[]
	);
	const handleCompletedAtChange = useCallback((dates) => {
		setDraftFilters((prev) => ({ ...prev, completedAt: clampDateRange(dates) }));
		setCompletedAtPickerDates(null);
	}, []);
	const handleCompletedAtOpenChange = useCallback((open) => {
		if (!open) setCompletedAtPickerDates(null);
	}, []);

	return {
		companyConfig,
		userType,
		problemTypes,
		fetchProblemTypes,
		history,
		loading,
		chartLoading,
		prioritiesLoading,
		error,
		prioritySummary,
		supplierPerformance,
		monthlyMetricData,
		monthlySpend,
		activeFilters,
		draftFilters,
		filterModalVisible,
		selectedPriorityId,
		minWoCount,
		setMinWoCount,
		viewMode,
		chartMetric,
		createdAtPickerDates,
		completedAtPickerDates,
		isAutoApprovalEnabled,
		priorityMap,
		handleArrowClick,
		handleChartMetricChange,
		navigateToSupplierDetail,
		openFilterModal,
		applyFilters,
		cancelFilters,
		removeProblemTypeFilter,
		handleRemoveCreatedAtFilter,
		handleRemoveCompletedAtFilter,
		handleProblemTypeChange,
		handleCreatedAtCalendarChange,
		handleCreatedAtChange,
		handleCreatedAtOpenChange,
		handleCompletedAtCalendarChange,
		handleCompletedAtChange,
		handleCompletedAtOpenChange,
	};
}
